import Highlight from '@tiptap/extension-highlight'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Paragraph from '@tiptap/extension-paragraph'
import { TableKit } from '@tiptap/extension-table'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import TextAlign from '@tiptap/extension-text-align'
import { Color, TextStyle } from '@tiptap/extension-text-style'
import Youtube from '@tiptap/extension-youtube'

import { TextSelection } from '@tiptap/pm/state'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Card } from 'antd'
import { useEffect, useRef, useState } from 'react'

import { FontFamily } from './extensions/FontFamily'
import { FontSize } from './extensions/FontSize'
import { ImageResizeModal } from './ImageResizeModal'
import { MenuBar } from './MenuBar'
import { useStyles } from './styles'

type Props = {
  value?: string
  onChange?: (value: string) => void
  readOnly?: boolean
  placeholder?: string
  enableImageModal?: boolean
  className?: string
}

const extensions = [
  TextStyle,
  Color,
  FontFamily,
  FontSize,
  TableKit,
  StarterKit.configure({
    paragraph: false,
    link: false
  }),
  Paragraph,
  Highlight.configure({ multicolor: true }),
  Image.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        width: {
          default: null,
          parseHTML: (element) => {
            const width = element.getAttribute('width')

            return width ? Number(width) : null
          },
          renderHTML: (attributes) => {
            if (!attributes.width) {
              return {}
            }
            return {
              width: String(attributes.width)
            }
          }
        },
        height: {
          default: null,
          parseHTML: (element) => {
            const height = element.getAttribute('height')

            return height ? Number(height) : null
          },
          renderHTML: (attributes) => {
            if (!attributes.height) {
              return {}
            }
            return {
              height: String(attributes.height)
            }
          }
        }
      }
    }
  }).configure({
    inline: true,
    allowBase64: true,
    HTMLAttributes: {
      class: 'editor-image'
    }
  }),
  Youtube.configure({
    width: 560,
    height: 315,
    controls: true,
    nocookie: false
  }),
  TaskList,
  TaskItem.configure({
    nested: true
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    defaultAlignment: 'left'
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: 'https',
    protocols: ['http', 'https'],
    isAllowedUri: (url, ctx) => {
      try {
        const parsedUrl = url.includes(':')
          ? new URL(url)
          : new URL(`${ctx.defaultProtocol}://${url}`)

        if (!ctx.defaultValidate(parsedUrl.href)) {
          return false
        }
        return true
      } catch {
        return false
      }
    }
  })
]

export const TipTapEditor = ({
  value,
  onChange,
  readOnly,
  placeholder,
  enableImageModal = false,
  className
}: Props) => {
  const { styles, cx } = useStyles()
  const [isResizeModalOpen, setIsResizeModalOpen] = useState(false)
  const isUpdatingFromProps = useRef(false)

  const editor = useEditor({
    extensions,
    content: value || '',
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    editable: !readOnly,
    editorProps: {
      handleClick: (view, pos, event) => {
        const { state } = view
        const target = event.target as HTMLElement

        const imgElement =
          target.tagName === 'IMG' ? target : target.closest('img')

        if (imgElement) {
          let imagePos: number | null = null

          const node = state.doc.nodeAt(pos)
          if (node && node.type.name === 'image') {
            imagePos = pos
          } else {
            const searchStart = Math.max(0, pos - 50)
            const searchEnd = Math.min(state.doc.content.size, pos + 50)

            state.doc.nodesBetween(searchStart, searchEnd, (node, nodePos) => {
              if (node.type.name === 'image' && imagePos === null) {
                imagePos = nodePos
              }
            })
          }

          if (imagePos !== null) {
            view.dispatch(
              state.tr.setSelection(
                TextSelection.near(state.doc.resolve(imagePos))
              )
            )
            setIsResizeModalOpen(true)
            return true
          }
        }

        const node = state.doc.nodeAt(pos)
        if (node && node.type.name === 'image') {
          setIsResizeModalOpen(true)
          return true
        }

        return false
      }
    },
    onUpdate: ({ editor }) => {
      if (!isUpdatingFromProps.current && onChange) {
        const html = editor.getHTML()
        onChange(html)
      }
    }
  })

  useEffect(() => {
    if (!editor) return

    const currentContent = editor.getHTML()
    const newValue = value || ''

    if (currentContent !== newValue) {
      isUpdatingFromProps.current = true
      editor.commands.setContent(newValue, { emitUpdate: false })
      setTimeout(() => {
        isUpdatingFromProps.current = false
      }, 0)
    }
  }, [value, editor])

  return (
    <Card>
      <MenuBar editor={editor} enableImageModal={enableImageModal} />
      <div className={cx(styles.editorContent, className)}>
        <EditorContent placeholder={placeholder} editor={editor} />
      </div>
      {isResizeModalOpen && (
        <ImageResizeModal
          editor={editor}
          onCancel={() => setIsResizeModalOpen(false)}
        />
      )}
    </Card>
  )
}
